 
/* eslint-disable quotes */
import { useMemo, useState, useEffect, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Alert, makeStyles, Typography, Button, Illustration, Card, CardContent, Tooltip } from '@ellucian/react-design-system/core';
import {
    spacing60,
    spacing30,
    spacing50,
    spacing40,
    fontSizeLarge,
    spacing80,
} from '@ellucian/react-design-system/core/styles/tokens';
import { useHistory } from 'react-router-dom';
import { routes, CONSTANTS } from '../Helpers/Configuration';
import { useApi } from '../ApiContext/';
import { useDispatch } from 'react-redux';
import isLoadingReducer, { startLoading, stopLoading } from '../../redux/shared/slices/isLoadingSlice';
import orderBy from 'lodash/orderBy';
import { SUPPORTED_FEATURES } from '@nsr/applicant-apply-shared/src/helpers/featureFlags';
import { Icon } from '@ellucian/ds-icons/lib';
import ApplicationCard from './ApplicationCard';
import { Link } from 'react-router-dom';
import DeleteConfirmationDialog from '../Helpers/DeleteConfirmationDialog';
import PageLoadIndicator from '../Helpers/PageLoadIndicator';
import { Form as ReduxStateStore } from '@nsr/react-form';
import HtmlFormattedMessage from '../HtmlFormattedMessage/HtmlFormattedMessage';
import useMaintenanceStatus from '../Helpers/useMaintenanceStatus';
import { getMaintenanceMessages } from '../Helpers/maintenanceMessageHelpers';
const useStyles = makeStyles({
    name: 'ApplicantHome'
})((theme) => ({
    root: {
        padding: `${spacing30} ${spacing30} ${spacing40} ${spacing30}`,
        width: '100%',
        [theme.breakpoints.up('md')]: {
            padding: spacing60,
            width: '100%',
        },
    },
    applicationList: {
        display: 'grid',
        gridGap: spacing40,
        gridTemplateRows: '1fr',
        gridArea: 'applications',
        [theme.breakpoints.up('md')]: {
            gridGap: spacing50,
        },
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
        },
    },
    content: {
        marginTop: spacing50,
        display: 'grid',
        gridGap: spacing50,
        gridTemplateColumns: '1fr',
        gridTemplateAreas: "'applications' 'profile'",
        width: '100%',
        maxWidth: '420px',
        marginLeft: 'auto',
        marginRight: 'auto',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '2fr 1fr',
            gridTemplateAreas: "'applications profile'",
            maxWidth: 'unset',
            marginLeft: 0,
            marginRight: 0,
        },
    },
    profileContent:{
        maxWidth: '350px',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    nameTag: {
        paddingBottom: spacing40
    },
    profileInfo: {
        display: 'flex',
        alignItems: 'center',
        paddingBottom: spacing40,
        '&:last-child': {
            paddingBottom: 0,
        },
    },
    icon: {
        marginRight: spacing30
    },
    button: {
        marginTop: spacing40,
        height: 'fit-content',
    },
    buttonDesktop: {
        display: 'none',
        [theme.breakpoints.up('md')]: {
            display: 'inline-flex',
        },
    },
    buttonMobile: {
        width: '100%',
        height: 'fit-content',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    noApplications: {
        padding: spacing40,
        textAlign: 'center',
    },
    illustration: {
        marginBottom: spacing40,
    },
    profileRegion: {
        gridArea: 'profile',
    },
    loadingOverlay: {
        position: 'fixed',
        inset: 0,
        zIndex: 2000, //ensure it is on top of everything
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pointerEvents: 'auto', // blocks mouse clicks
    },
    maintenanceBanner: {
        marginBottom: spacing40,
    },
    maintenancePage: {
        alignItems: 'center',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        maxWidth: '48rem',
        minHeight: 'calc(100vh - 8rem)',
        margin: '0 auto',
        padding: `${spacing80} ${spacing40}`,
        textAlign: 'center',
    },
    maintenanceMessage: {
        fontSize: fontSizeLarge,
        lineHeight: 1.6,
    },
    maintenanceSchedule: {
        marginTop: spacing40,
    }

}));
const ApplicationHomeWrapper = props => {
    const additionalSlices = useMemo(() => ({
        isLoading: isLoadingReducer,
    }), []);
    return (
        <ReduxStateStore additionalSlices={additionalSlices}>
            <ApplicantHomeWrapper {...props} />
        </ReduxStateStore>
    );
};
const ApplicantHome = () => {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const intl = useIntl();
    const { formatMessage } = intl;
    const [showWithdraw, setShowWithdraw] = useState(false);
    const [currentApplication, setCurrentApplication] = useState({});
    const [applications, setApplications] = useState([]);
    const [user] = useState(window.PRELOADED_STATE?.user ?? {});
    const {featureSupported} = window.PRELOADED_STATE ?? {};
    const history = useHistory();
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const initialMaintenanceStatus = window.PRELOADED_STATE?.globalSettings?.maintenanceStatus;
    const { maintenanceStatus, status } = useMaintenanceStatus(initialMaintenanceStatus);
    const {
        maintenanceMessage,
        warningBannerMessage,
    } = getMaintenanceMessages(intl, maintenanceStatus);
    // Create a ref for the main content region
    const mainRef = useRef(null);

    const accessApplication = async application => {
        // If status is INCOMPLETE, navigate directly to Supporting Documents
        if (application.status === CONSTANTS.applicationFormStatuses.INCOMPLETE) {
            history.push(routes.applicationFormSupportingDocumentsPage.replace(':id', application.id));
        } else {
            history.push(routes.applicationFormStartWithId(application.id));
        }
    };
    const refreshApplications = async () => {
        const applicationsFromApi = await api.applicationForms.getAll();
        if (featureSupported?.[SUPPORTED_FEATURES.applicationReview]) {
            if (applicationsFromApi?.length > 0) {
                const decisionsFromApi = await api.decisions.getStatus();
                if (decisionsFromApi?.length > 0) {
                    applicationsFromApi.map(application => {
                        const decision = decisionsFromApi.find(decision => decision?.applicationId === application?.id);
                        application.decisionStatus = decision?.decisionStatus ?? '';
                        application.decisionReason = decision?.decisionReason ?? '';
                        application.offerStatus = decision?.offerStatus ?? '';
                    });
                }
            }
        }
        setApplications(orderBy(applicationsFromApi, ['updatedAt'], ['desc']));
    };
   
    useEffect(() => {
        (async () => {
            setIsLoading(true);
            dispatch(startLoading());
            await refreshApplications();
            setIsLoading(false);
            dispatch(stopLoading());
            if (mainRef.current) {
                mainRef.current.focus();
            }
        })();
    }, []);

    const nameValues = useMemo(() => {
        return {
            firstName: user.firstName,
            lastName: user.lastName,
        };
    }, [user]);

    const handleWithdraw = (applicationId) => {
        setCurrentApplication(applicationId);
        setShowWithdraw(true);
    };

    const withdrawApplication = async () => {
        setIsLoading(true);
        dispatch(startLoading());
        setShowWithdraw(false);
        await api.applicationForms.withdraw(currentApplication);
        await refreshApplications();
        setIsLoading(false);
        dispatch(stopLoading());
    };

    const combineCodeAndNumber = (value) => {
        if (!value) {
            return '';
        }
        else if (typeof value === 'string') {
            return value;
        }
        else {
            if(value.code && value.number) {
                return `+${value.code}${value.number}`;   
            }
            else if(value.number) {
                return value.number;
            }
            else { 
                return '';
            }            
        }
    };

    return (
        <div className={classes.root} ref={mainRef} tabIndex="-1" role="main">
            {status === CONSTANTS.maintenanceStatus.ACTIVE ?
                <div className={classes.maintenancePage}>
                    <Typography variant="h1"><FormattedMessage id="maintenancePageTitle" /></Typography>
                    {maintenanceMessage && <div className={classes.maintenanceMessage}><HtmlFormattedMessage htmlString={maintenanceMessage} normalizeSpaces /></div>}
                </div> :
                <>
                    {status === CONSTANTS.maintenanceStatus.WARNING_WINDOW && warningBannerMessage &&
                <div className={classes.maintenanceBanner}>
                    <Alert alertType="warning" open={true} text={warningBannerMessage} userDismissable={false} />
                </div>
                    }
                    {isLoading && (
                    // Wrap the loader in a container that is hidden from assistive technologies
                        (<div className={classes.loadingOverlay} aria-hidden="true">
                            <PageLoadIndicator />
                        </div>)
                    )}
                    <DeleteConfirmationDialog
                        isOpen={showWithdraw}
                        onPrimaryClick={withdrawApplication}
                        onSecondaryClick={() => setShowWithdraw(false)}
                        title={formatMessage({ id: 'applicantHomeWithdrawConfirmationTitle' })}
                        contentText={formatMessage({ id: 'applicantHomeWithdrawConfirmationMessage' })}
                        primaryActionText={formatMessage({ id: 'applicationSubmissionYes' })}
                        secondaryActionText={formatMessage({ id: 'applicationSubmissionNo' })}
                    />
                    <div className={classes.header}>
                        <Typography variant="h1" className={classes.emailTag}>
                            <FormattedMessage id="applicationsHome" />
                        </Typography>
                        <Button
                            className={`${classes.button} ${classes.buttonDesktop}`}
                            onClick={() => { if (!isLoading) history.push(routes.newApplication); }}
                            disabled={isLoading}>
                            <FormattedMessage id="applicantHomeNewApplication" />
                        </Button>
                    </div>
                    <div className={classes.content}>
                        {/* Moved profile div before applicationList to adjust DOM/tab order */}
                        <div className={classes.profileRegion} role="region" aria-label="Applicant Profile">
                            <Card accent="secondary">
                                <CardContent>
                                    <Tooltip title={nameValues.firstName + ' ' + nameValues.lastName}>
                                        <Typography
                                            component="h2"
                                            variant="h2"
                                            className={classes.profileContent}
                                            tabIndex={0}
                                            aria-label={`Applicant Name: ${nameValues.firstName} ${nameValues.lastName}`}
                                            role="heading"
                                            aria-level="2"
                                        >
                                            <FormattedMessage id="applicantHomeApplicantName" values={nameValues} />
                                        </Typography>
                                    </Tooltip>

                                    <Typography
                                        component="div"
                                        className={classes.profileInfo}
                                        tabIndex={0}
                                        aria-label={`Email address: ${user.email}`}
                                        role="heading"
                                        aria-level="3"
                                    >
                                        <Icon name="email" className={classes.icon} aria-hidden="true" />
                                        <div className={classes.profileContent}>{user.email}</div>
                                    </Typography>

                                    <Typography
                                        component="div"
                                        className={classes.profileInfo}
                                        tabIndex={0}
                                        aria-label={`Phone number: ${combineCodeAndNumber(user.phone)}`}
                                    >
                                        <Icon name="phone" className={classes.icon} aria-hidden="true" />
                                        <div className={classes.profileContent} dir="ltr">
                                            {combineCodeAndNumber(user.phone)}
                                        </div>
                                    </Typography>

                                </CardContent>
                            </Card>
                        </div>
                        <div
                            className={classes.applicationList}
                            role='region'
                            aria-busy={isLoading ? 'true' : 'false'}
                        >
                            {applications.length === 0 && !isLoading &&
                        <div className={classes.noApplications}>
                            <div className={classes.illustration}>
                                <Illustration name="no-tasks" />
                            </div>
                            <Typography component="h2" variant="h2">
                                <FormattedMessage id="applicantHomeNoApplicationsDetail" />
                            </Typography>
                            <br />
                            <Typography component="div" variant="body1">
                                <Link to={routes.newApplication}>
                                    <FormattedMessage id="applicantHomeStartApplication" />
                                </Link>
                            </Typography>
                        </div>
                            }
                            {applications.map(it => {
                                return (
                                    <div key={it.id}>
                                        <ApplicationCard
                                            application={it}
                                            accessApplication={accessApplication}
                                            withdrawApplication={handleWithdraw}
                                        />
                                    </div>
                                );
                            })}
                            {applications.length > 0 && (
                                <Button
                                    className={classes.buttonMobile}
                                    onClick={() => { if (!isLoading) history.push(routes.newApplication); }}
                                    disabled={isLoading}
                                >
                                    <FormattedMessage id="applicantHomeNewApplication" />
                                </Button>
                            )}
                        </div>
                    </div>
                </>
            }
        </div>
    );
};

const ApplicantHomeWrapper = ApplicantHome;

export default ApplicationHomeWrapper;
